import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import sum from 'lodash/sum';

import {
  REGS,
  ROAS,
  ROAS_D0,
  ROAS_D7,
  ROAS_D30,
  ROAS_D3,
  RETENTION_D1,
  RETENTION_D7,
  RETENTION_D30,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';
import { precision } from 'scenes/MarketingPlanning/utils';

const calcAllocatedBudget = (childTypes) =>
  sum(childTypes.map(({ budgetAllocated }) => budgetAllocated)) || 0;

const calcChildBudget = (childTypes) =>
  sum(childTypes.map(({ budget }) => budget)) || 0;

const calcRetention = ({ childMetrics, childMetricRegs, cMetricRegs }) => {
  const weightedSum = sum(
    childMetrics.map(
      ({ metricCalcValue, metricValue }, i) =>
        (metricCalcValue || metricValue) * childMetricRegs[i]
    )
  );

  if (!weightedSum || !cMetricRegs) {
    return 0;
  }

  return precision(weightedSum / cMetricRegs, 1);
};

const calcROAS = ({ childMetrics, budgetAllocated }) => {
  const weightedSum = sum(
    childMetrics.map(
      ({
        metricCalcValue,
        metricValue,
        budgetAllocated: childAllocatedBudget,
      }) => (metricCalcValue || metricValue) * childAllocatedBudget
    )
  );

  if (!weightedSum || !budgetAllocated) {
    return 0;
  }

  return precision(weightedSum / budgetAllocated, 1);
};

const calcMetric = ({
  key,
  childMetrics,
  budgetAllocated,
  cMetricRegs,
  childMetricRegs,
}) => {
  switch (key) {
    case REGS:
      return cMetricRegs;
    case RETENTION_D1:
    case RETENTION_D7:
    case RETENTION_D30:
      return calcRetention({ childMetrics, childMetricRegs, cMetricRegs });
    case ROAS:
    case ROAS_D0:
    case ROAS_D7:
    case ROAS_D30:
    case ROAS_D3:
      return calcROAS({
        childMetrics,
        budgetAllocated,
      });
  }
};

const calcBudgetSum = (activityTypes) =>
  sum(map(activityTypes, ({ budget }) => budget));

const calcParentActivityType = ({ activityTypes, nodes }, type) => {
  const { budget } = activityTypes[type];
  const childTypes = nodes
    .map((child) => child.activityTypes[type])
    .filter((i) => i);
  const budgetLeft = budget - calcChildBudget(childTypes);

  return {
    budget,
    budgetLeft,
  };
};

const calcActivityType = ({
  initialActivityType,
  childTypes,
  parent,
  settings: { countDays },
}) => {
  const { budget, metrics, comment = '' } = initialActivityType;
  const { budget: budgetParent, budgetLeft: budgetParentLeft } = parent;

  const budgetLeft = budget - calcChildBudget(childTypes);

  const budgetAllocated = !isEmpty(childTypes)
    ? calcAllocatedBudget(childTypes)
    : budget;

  const budgetPercent =
    budget && budgetParent ? precision((budget / budgetParent) * 100, 1) : 0;

  const budgetRest = Math.max(budgetParentLeft, 0) + budget;

  const budgetDaily =
    budget && countDays ? precision(budget / countDays, 1) : 0;

  const cMetricRegs =
    sum(childTypes.map(({ cMetrics }) => cMetrics[REGS].value)) || 0;

  const childMetricRegs = childTypes.map(
    ({ cMetrics, metrics }) => cMetrics[REGS].value || metrics[REGS]
  );

  const cMetrics = mapValues(metrics, (value, metricName) => {
    const childMetrics = childTypes.map(
      ({ metrics, cMetrics, budgetAllocated: childAllocatedBudget }) => {
        return {
          budgetAllocated: childAllocatedBudget,
          metricValue: metrics[metricName],
          metricCalcValue: cMetrics[metricName].value,
        };
      }
    );

    if (isEmpty(childMetrics)) {
      return {
        value,
        less: false,
      };
    }

    const cMetricValue = calcMetric({
      key: Number(metricName),
      childMetrics,
      budgetAllocated,
      cMetricRegs,
      childMetricRegs,
    });

    return {
      value: cMetricValue,
      less: cMetricValue < value,
    };
  });

  return {
    budget,
    budgetAllocated,
    budgetLeft,
    metrics,
    cMetrics,
    comment,
    budgetParent,
    budgetRest,
    budgetPercent,
    budgetDaily,
  };
};

const getCommonName = (nodes) =>
  Array.isArray(nodes) && nodes.length === 1 ? 'All' : 'Other';

const calculateDepth = (values, parentNode, settings) => {
  const {
    entityId: _entityId,
    depth,
    activityTypes,
    nodes,
    name,
    ...node
  } = values;
  const entityId = _entityId ?? -1;

  const { meta, metricTypes } = settings;

  const cNodes = nodes
    .map((node) => calculateDepth(node, values, settings))
    .filter((i) => i);

  const cActivityTypes = meta.activityTypes.reduce((acc, type) => {
    const activityType = activityTypes[type];
    if (!activityType) {
      return acc;
    }

    const childTypes = cNodes
      .map((child) => child.activityTypes[type])
      .filter((i) => i);

    const parent = parentNode ? calcParentActivityType(parentNode, type) : {};

    return {
      ...acc,
      [type]: calcActivityType({
        initialActivityType: activityType,
        childTypes,
        parent,
        settings: {
          countDays: meta.countDays,
        },
      }),
    };
  }, {});

  if (isEmpty(cActivityTypes)) {
    return null;
  }

  const budgetSum = calcBudgetSum(cActivityTypes);

  return {
    ...node,
    entityId,
    depth,
    name: name || getCommonName(parentNode.nodes),
    activityTypes: cActivityTypes,
    nodes: cNodes,
    budgetSum,
    metricTypes,
  };
};

export default calculateDepth;
